import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  buildAutotestAnalysisReport,
  getAutotestAnalysisReports,
  runAutotestDailyReport,
  runAutotestWeeklyReport,
} from '../../api/autotests'
import { useToast } from '../../contexts/ToastContext'
import { useI18n } from '../../i18n'
import { FilterGroup, FiltersRow, PageCard, PageSection, Select } from '../../components/ui'
import { AccessDeniedView, ExpandableText, JsonViewer, StatusBadge } from './components'
import { asList, extractTaskId, formatLocalDate, hasRunning, isIntInRange } from './helpers'
import '../../styles/pages/page-common.css'
import '../../styles/pages/autotests.css'

const REPORT_TYPE_LABELS = {
  ad_hoc: 'Ручной',
  daily: 'Ежедневный',
  weekly: 'Еженедельный',
}

const INITIAL_BUILD_FORM = {
  report_type: 'ad_hoc',
  hours: '24',
  scope_type: '',
  fan_id: '',
  model_id: '',
  is_test: false,
}

export default function AutotestsAnalysisPage() {
  const { t } = useI18n()
  const { showToast } = useToast()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [accessDenied, setAccessDenied] = useState(false)

  const [reports, setReports] = useState([])
  const [expandedReports, setExpandedReports] = useState({})
  const [statusFilter, setStatusFilter] = useState('')
  const [pollReports, setPollReports] = useState(false)
  const [reportsLimit, setReportsLimit] = useState(50)

  const [runningDaily, setRunningDaily] = useState(false)
  const [runningWeekly, setRunningWeekly] = useState(false)
  const [buildingReport, setBuildingReport] = useState(false)

  const [buildForm, setBuildForm] = useState(INITIAL_BUILD_FORM)
  const [buildFormError, setBuildFormError] = useState('')

  const statusOptions = useMemo(
    () => [{ value: '', label: t('logs.status.all') }, ...['COMPLETED', 'FAILED', 'RUNNING', 'ERROR'].map((status) => ({ value: status, label: t(`autotests.status.${status}`) }))],
    [t],
  )

  const filteredReports = useMemo(() => {
    if (!statusFilter) return reports
    return reports.filter((item) => String(item?.status || '').toUpperCase() === statusFilter)
  }, [reports, statusFilter])

  async function loadReports() {
    setLoading(true)
    setError('')
    setAccessDenied(false)
    try {
      const res = await getAutotestAnalysisReports({ limit: reportsLimit, offset: 0 })
      const next = asList(res)
      setReports(next)
      if (!hasRunning(next)) setPollReports(false)
    } catch (err) {
      if (err?.status === 401 || err?.status === 403) {
        setAccessDenied(true)
      } else {
        setError(err?.message || t('common.error'))
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadReports()
  }, [])

  useEffect(() => {
    if (!pollReports) return
    const timer = setTimeout(() => {
      loadReports()
    }, 10000)
    return () => clearTimeout(timer)
  }, [pollReports, reports])

  async function runTask(handler, setLoadingState) {
    setLoadingState(true)
    try {
      const res = await handler()
      const taskId = extractTaskId(res)
      if (taskId) showToast(t('autotests.toast.taskStarted', { task_id: taskId }), 'success')
      else showToast(t('autotests.toast.started'), 'success')
      setPollReports(true)
      loadReports()
    } catch (err) {
      if (err?.status === 401 || err?.status === 403) {
        setAccessDenied(true)
      } else {
        showToast(err?.message || t('common.error'), 'error')
      }
    } finally {
      setLoadingState(false)
    }
  }

  function validateBuildForm() {
    if (!isIntInRange(buildForm.hours, 1, 168)) return t('autotests.validation.hours')
    return ''
  }

  async function handleBuildReport(e) {
    e.preventDefault()
    const validationError = validateBuildForm()
    setBuildFormError(validationError)
    if (validationError) return

    const payload = {
      report_type: buildForm.report_type,
      hours: Number(buildForm.hours),
      is_test: Boolean(buildForm.is_test),
    }
    if (buildForm.scope_type === 'fan') {
      payload.scope_type = 'fan'
      payload.scope_id = String(Number(buildForm.fan_id))
    }
    if (buildForm.scope_type === 'model') {
      payload.scope_type = 'model'
      payload.scope_id = String(Number(buildForm.model_id))
    }

    setBuildingReport(true)
    try {
      const res = await buildAutotestAnalysisReport(payload)
      const taskId = extractTaskId(res)
      if (taskId) showToast(t('autotests.toast.taskStarted', { task_id: taskId }), 'success')
      else showToast(t('autotests.toast.started'), 'success')
      setPollReports(true)
      loadReports()
    } catch (err) {
      if (err?.status === 401 || err?.status === 403) {
        setAccessDenied(true)
      } else {
        setBuildFormError(err?.message || t('common.error'))
      }
    } finally {
      setBuildingReport(false)
    }
  }

  if (accessDenied) return <AccessDeniedView />

  return (
    <PageSection
      className="autotests-page"
      title={t('autotests.nav.analysis')}
      subtitle={t('autotests.analysis.subtitle')}
    >
      {error && <p className="form-error">{error}</p>}

      <PageCard className="autotests-panel">
        <h3 className="autotests-panel-title">{t('autotests.analysis.tasks')}</h3>
        <div className="autotests-actions-row" style={{ gap: '12px', alignItems: 'flex-start' }}>
          <div className="autotests-quick-task">
            <button type="button" className="btn btn-secondary" disabled={runningDaily} onClick={() => runTask(runAutotestDailyReport, setRunningDaily)}>
              {t('autotests.analysis.daily')}
            </button>
            <span className="autotests-hint">GPT-анализ логов бота за последние 24 часа</span>
          </div>
          <div className="autotests-quick-task">
            <button type="button" className="btn btn-secondary" disabled={runningWeekly} onClick={() => runTask(runAutotestWeeklyReport, setRunningWeekly)}>
              {t('autotests.analysis.weekly')}
            </button>
            <span className="autotests-hint">GPT-анализ логов бота за последние 7 дней</span>
          </div>
        </div>
      </PageCard>

      <PageCard className="autotests-panel">
        <h3 className="autotests-panel-title">{t('autotests.analysis.buildReport')}</h3>
        <form className="autotests-form autotests-build-form" onSubmit={handleBuildReport}>
          <p className="autotests-muted" style={{ marginBottom: '16px', fontSize: '0.9rem' }}>
            Сгенерировать аналитический отчёт по логам за произвольный период. GPT проанализирует все запуски графов, ответы бота и ошибки.
          </p>

          <label className="form-label">Период анализа (часов назад от текущего момента)</label>
          <input
            type="number"
            min={1}
            max={168}
            value={buildForm.hours}
            onChange={(e) => {
              setBuildForm((prev) => ({ ...prev, hours: e.target.value }))
              setBuildFormError('')
            }}
            style={{ maxWidth: '200px' }}
          />
          <span className="autotests-hint" style={{ marginTop: '4px', display: 'block' }}>
            Например: 24 = последние сутки, 72 = последние 3 дня, 168 = неделя
          </span>

          <label className="autotests-checkbox-row" style={{ marginTop: '16px' }}>
            <input
              type="checkbox"
              checked={buildForm.is_test}
              onChange={(e) => setBuildForm((prev) => ({ ...prev, is_test: e.target.checked }))}
            />
            Анализировать только тестовые чаты
          </label>
          <span className="autotests-hint" style={{ marginLeft: '24px' }}>
            Если включено — в отчёт попадут только логи тестовых фанов (is_test). Если выключено — анализируются логи реальных переписок.
          </span>

          {buildFormError && <p className="form-error">{buildFormError}</p>}

          <div style={{ marginTop: '16px' }}>
            <button type="submit" className="btn btn-primary" disabled={buildingReport}>
              {buildingReport ? t('common.loading') : t('autotests.analysis.buildReport')}
            </button>
          </div>
        </form>
      </PageCard>

      <FiltersRow style={{ marginTop: '24px' }}>
        <FilterGroup label={t('autotests.filters.status')}>
          <Select
            className="filter-select"
            value={statusFilter}
            onChange={(value) => setStatusFilter(value || '')}
            options={statusOptions}
            dropdownPosition="down"
          />
        </FilterGroup>
      </FiltersRow>

      <PageCard className="autotests-panel">
        <h3 className="autotests-panel-title">{t('autotests.analysis.reports')}</h3>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('autotests.form.reportType')}</th>
                <th>{t('autotests.form.period')}</th>
                <th>{t('autotests.status')}</th>
                <th>{t('autotests.form.summary')}</th>
              </tr>
            </thead>
            <tbody>
              {loading && reports.length === 0 ? (
                <tr>
                  <td colSpan={5} className="autotests-muted">{t('common.loading')}</td>
                </tr>
              ) : filteredReports.length === 0 ? (
                <tr>
                  <td colSpan={5} className="autotests-muted">{t('autotests.empty')}</td>
                </tr>
              ) : (
                filteredReports.map((item) => {
                  const isOpen = Boolean(expandedReports[item.id])
                  return (
                    <Fragment key={item.id}>
                      <tr>
                        <td>
                          <button
                            type="button"
                            className="autotests-link-btn"
                            onClick={() => setExpandedReports((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                          >
                            #{item.id}
                          </button>
                        </td>
                        <td>{REPORT_TYPE_LABELS[item.report_type] || item.report_type || '—'}</td>
                        <td>
                          {item.period_start && item.period_end
                            ? `${formatLocalDate(item.period_start)} → ${formatLocalDate(item.period_end)}`
                            : (item.period || `${item.hours || '—'}h`)}
                        </td>
                        <td><StatusBadge status={item.status} /></td>
                        <td>
                          <div className="autotests-summary-cell">
                            <span>{(item.summary || '—').length > 100 ? (item.summary || '').slice(0, 100) + '...' : (item.summary || '—')}</span>
                            {(item.summary || '').length > 100 && (
                              <button
                                type="button"
                                className="autotests-link-btn"
                                onClick={() => setExpandedReports((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                              >
                                {isOpen ? 'Скрыть' : 'Показать больше'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={5}>
                            <div className="autotests-report-expanded">
                              {item.summary && (
                                <>
                                  <h4>Сводка</h4>
                                  <p style={{ whiteSpace: 'pre-wrap', marginBottom: '16px' }}>{item.summary}</p>
                                </>
                              )}
                              {item.state_transitions_analysis && (
                                <>
                                  <h4>Анализ переходов состояний</h4>
                                  <JsonViewer value={item.state_transitions_analysis} />
                                </>
                              )}
                              {item.anomalies && (
                                <>
                                  <h4>{t('autotests.form.anomalies')}</h4>
                                  <JsonViewer value={item.anomalies} />
                                </>
                              )}
                              {(item.recommendations || item.improvements) && (
                                <>
                                  <h4>{t('autotests.form.recommendations')}</h4>
                                  <JsonViewer value={item.recommendations || item.improvements || []} />
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </PageCard>

      <div className="autotests-actions-row">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            const next = reportsLimit + 50
            setReportsLimit(next)
            getAutotestAnalysisReports({ limit: next, offset: 0 })
              .then((res) => setReports(asList(res)))
              .catch(() => undefined)
          }}
        >
          {t('common.loadMore')}
        </button>
      </div>
    </PageSection>
  )
}
